/* ============================================================
   video-by studiolab — viewport playback + audio fade engine
   FILE:    assets/sl-video.js
   VERSION: 1.0.0

   WHAT IT DOES
   - Plays a video when it enters the viewport, pauses when it leaves.
   - Fades the audio up on enter and down on exit (rAF volume tween).
   - Picks the desktop or mobile source at runtime from matchMedia, so
     only one file is ever downloaded.

   THE AUDIO RULE (browser policy, not a Shopify limitation)
   No browser will start audio before the visitor has interacted with the
   page. Chrome requires user activation (click / tap / keydown — scrolling
   does NOT count); Safari ships "Stop Media with Sound" by default; iOS
   needs a real gesture. So playback ALWAYS starts muted, and the audio
   only fades in once activation has been observed. Until then the sound
   button is the affordance. After the first tap anywhere, every later
   viewport entry fades in on its own.

   Public surface (inline onclick from the section):
     window.slVideo.toggleSound(sectionId)
   ============================================================ */
(function () {
  'use strict';

  if (window.slVideo && window.slVideo.__loaded) return;

  var instances = {};
  var userActivated = false;
  var activationBound = false;

  var inEditor = !!(window.Shopify && window.Shopify.designMode);
  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── user activation ──────────────────────────────────────── */

  function bindActivation() {
    if (activationBound) return;
    activationBound = true;

    var onActivate = function () {
      if (userActivated) return;
      userActivated = true;
      document.removeEventListener('pointerdown', onActivate, true);
      document.removeEventListener('keydown', onActivate, true);
      document.removeEventListener('touchend', onActivate, true);

      // Any instance already in view and waiting for permission gets it now.
      Object.keys(instances).forEach(function (id) {
        var inst = instances[id];
        if (inst && inst.inView && inst.wantsSound) applySound(inst, true);
      });
    };

    document.addEventListener('pointerdown', onActivate, true);
    document.addEventListener('keydown', onActivate, true);
    document.addEventListener('touchend', onActivate, true);
  }

  /* ── helpers ──────────────────────────────────────────────── */

  function isMobile(inst) {
    return window.matchMedia('(max-width: ' + inst.breakpoint + 'px)').matches;
  }

  function readBool(el, name, fallback) {
    var v = el.getAttribute(name);
    if (v === null || v === '') return fallback;
    return v === 'true';
  }

  /* The merchant's threshold is "how much of the video is showing". When the
     video is TALLER than the viewport that ratio is capped below 1 — a 9:16
     video on a short phone can top out around 0.45 — so a literal 0.5 would
     never fire. Cap the requirement at what is physically reachable. */
  function effectiveThreshold(inst, entry) {
    var h = entry.boundingClientRect.height;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    if (!h || !vh) return inst.threshold;
    var reachable = vh / h;
    if (reachable >= 1) return inst.threshold;
    return Math.min(inst.threshold, reachable * 0.8);
  }

  function cancelFade(inst) {
    if (inst.fadeFrame) {
      cancelAnimationFrame(inst.fadeFrame);
      inst.fadeFrame = null;
    }
  }

  /* Tween the volume. `setter` is provider-specific (native / YT / Vimeo). */
  function fadeVolume(inst, from, to, done) {
    cancelFade(inst);

    var duration = inst.fadeDuration;
    if (duration <= 0 || reduceMotion) {
      inst.setVolume(to);
      if (done) done();
      return;
    }

    var start = null;
    var step = function (now) {
      if (start === null) start = now;
      var t = Math.min((now - start) / duration, 1);
      // easeOutQuad — audio ramps feel abrupt on a linear curve
      var eased = 1 - (1 - t) * (1 - t);
      inst.setVolume(from + (to - from) * eased);
      if (t < 1) {
        inst.fadeFrame = requestAnimationFrame(step);
      } else {
        inst.fadeFrame = null;
        if (done) done();
      }
    };
    inst.fadeFrame = requestAnimationFrame(step);
  }

  /* Decide whether sound may play right now, and act on it. */
  function applySound(inst, fadeIn) {
    var allowed = inst.wantsSound && userActivated && !inEditor;

    if (!allowed) {
      inst.setMuted(true);
      inst.setVolume(0);
      updateButton(inst, false);
      return;
    }

    inst.setMuted(false);
    if (fadeIn) {
      fadeVolume(inst, inst.currentVolume, inst.targetVolume);
    } else {
      inst.setVolume(inst.targetVolume);
    }
    updateButton(inst, true);
  }

  function updateButton(inst, on) {
    if (!inst.soundBtn) return;
    inst.soundBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  /* ── native <video> provider ──────────────────────────────── */

  function mountNative(inst) {
    if (inst.media) return;

    var which = isMobile(inst) ? 'mobile' : 'desktop';
    var tpl = inst.root.querySelector('[data-sl-video-sources="' + which + '"]');
    if (!tpl || !tpl.content || !tpl.content.querySelector('source')) {
      tpl = inst.root.querySelector('[data-sl-video-sources="desktop"]');
    }
    if (!tpl) return;

    var video = document.createElement('video');
    video.className = inst.prefix + '__media';
    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.preload = 'metadata';
    video.loop = inst.loop;
    video.controls = inst.controls;
    video.setAttribute('disablepictureinpicture', '');
    if (inst.label) video.setAttribute('aria-label', inst.label);

    video.appendChild(tpl.content.cloneNode(true));

    video.addEventListener('playing', function () {
      inst.root.classList.add('is-playing');
    });

    inst.media = video;
    inst.loadedFor = which;
    inst.root.insertBefore(video, inst.root.firstChild);

    inst.setVolume = function (v) {
      inst.currentVolume = v;
      try {
        video.volume = Math.max(0, Math.min(1, v));
      } catch (e) {
        /* some browsers reject volume writes on iOS — muted state still applies */
      }
    };
    inst.setMuted = function (m) {
      video.muted = m;
    };
    inst.play = function () {
      var p = video.play();
      if (p && p.catch) p.catch(function () { /* autoplay refused — poster stays */ });
    };
    inst.pause = function () {
      video.pause();
    };

    inst.root.classList.add('is-ready');
  }

  /* Swap the file when the breakpoint changes (desktop <-> mobile). */
  function remountNative(inst) {
    if (!inst.media) return;
    var which = isMobile(inst) ? 'mobile' : 'desktop';
    if (which === inst.loadedFor) return;

    cancelFade(inst);
    inst.media.pause();
    inst.media.remove();
    inst.media = null;
    inst.root.classList.remove('is-playing');
    mountNative(inst);
    if (inst.inView) startPlayback(inst);
  }

  /* ── external providers (YouTube / Vimeo) ─────────────────── */

  var apiPromises = {};

  function loadScript(src, key) {
    if (apiPromises[key]) return apiPromises[key];
    apiPromises[key] = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return apiPromises[key];
  }

  function youtubeReady() {
    if (window.YT && window.YT.Player) return Promise.resolve();
    if (!apiPromises.ytReady) {
      apiPromises.ytReady = new Promise(function (resolve) {
        var prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = function () {
          if (typeof prev === 'function') prev();
          resolve();
        };
      });
      loadScript('https://www.youtube.com/iframe_api', 'yt').catch(function () {});
    }
    return apiPromises.ytReady;
  }

  function mountExternal(inst) {
    if (inst.media || inst.mounting) return;

    var cfg = inst.root.querySelector('[data-sl-video-external]');
    if (!cfg) return;

    var which = isMobile(inst) ? 'mobile' : 'desktop';
    var provider = cfg.getAttribute('data-provider-' + which);
    var videoId = cfg.getAttribute('data-id-' + which);
    if (!provider || !videoId) {
      provider = cfg.getAttribute('data-provider-desktop');
      videoId = cfg.getAttribute('data-id-desktop');
      which = 'desktop';
    }
    if (!provider || !videoId) return;

    inst.mounting = true;
    inst.loadedFor = which;

    if (provider === 'youtube') mountYouTube(inst, videoId);
    else if (provider === 'vimeo') mountVimeo(inst, videoId);
    else inst.mounting = false;
  }

  function mountYouTube(inst, videoId) {
    var host = document.createElement('div');
    host.className = inst.prefix + '__media';
    inst.root.insertBefore(host, inst.root.firstChild);

    youtubeReady().then(function () {
      var vars = {
        autoplay: 0,
        controls: inst.controls ? 1 : 0,
        mute: 1,
        playsinline: 1,
        rel: 0,
        modestbranding: 1,
        disablekb: inst.controls ? 0 : 1
      };
      // YouTube only loops a single video when it is also its own playlist.
      if (inst.loop) {
        vars.loop = 1;
        vars.playlist = videoId;
      }

      var player = new window.YT.Player(host, {
        videoId: videoId,
        playerVars: vars,
        events: {
          onReady: function () {
            inst.media = player;
            inst.mounting = false;
            inst.root.classList.add('is-ready');

            inst.setVolume = function (v) {
              inst.currentVolume = v;
              player.setVolume(Math.max(0, Math.min(1, v)) * 100);
            };
            inst.setMuted = function (m) {
              if (m) player.mute();
              else player.unMute();
            };
            inst.play = function () {
              player.playVideo();
            };
            inst.pause = function () {
              player.pauseVideo();
            };

            player.mute();
            player.setVolume(0);
            if (inst.inView) startPlayback(inst);
          },
          onStateChange: function (e) {
            if (e.data === window.YT.PlayerState.PLAYING) {
              inst.root.classList.add('is-playing');
            }
          }
        }
      });
    });
  }

  function mountVimeo(inst, videoId) {
    var iframe = document.createElement('iframe');
    iframe.className = inst.prefix + '__media';
    // NOTE: deliberately NOT using Vimeo's background=1 — it force-mutes the
    // player and blocks unmuting, which would defeat this feature entirely.
    // controls=0 gives the same clean look while leaving audio reachable.
    iframe.src =
      'https://player.vimeo.com/video/' +
      encodeURIComponent(videoId) +
      '?controls=' + (inst.controls ? '1' : '0') +
      '&muted=1&autoplay=0&loop=' + (inst.loop ? '1' : '0') +
      '&playsinline=1&title=0&byline=0&portrait=0&dnt=1';
    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
    iframe.setAttribute('frameborder', '0');
    if (inst.label) iframe.setAttribute('title', inst.label);
    inst.root.insertBefore(iframe, inst.root.firstChild);

    loadScript('https://player.vimeo.com/api/player.js', 'vimeo')
      .then(function () {
        var player = new window.Vimeo.Player(iframe);
        inst.media = player;
        inst.mounting = false;
        inst.root.classList.add('is-ready');

        inst.setVolume = function (v) {
          inst.currentVolume = v;
          player.setVolume(Math.max(0, Math.min(1, v)));
        };
        inst.setMuted = function (m) {
          player.setMuted(m);
        };
        inst.play = function () {
          var p = player.play();
          if (p && p.catch) p.catch(function () {});
        };
        inst.pause = function () {
          player.pause();
        };

        player.on('play', function () {
          inst.root.classList.add('is-playing');
        });

        player.setMuted(true);
        player.setVolume(0);
        if (inst.inView) startPlayback(inst);
      })
      .catch(function () {
        inst.mounting = false;
      });
  }

  /* ── playback control ─────────────────────────────────────── */

  function startPlayback(inst) {
    if (!inst.media || !inst.autoplay) return;
    // Reduced motion: never start on its own. Controls are the way in.
    if (reduceMotion) return;

    inst.setMuted(true);
    inst.setVolume(0);
    inst.play();
    applySound(inst, true);
  }

  function stopPlayback(inst) {
    if (!inst.media) return;

    var finish = function () {
      inst.setMuted(true);
      if (inst.pause) inst.pause();
    };

    if (inst.currentVolume > 0) {
      fadeVolume(inst, inst.currentVolume, 0, finish);
    } else {
      cancelFade(inst);
      finish();
    }
    updateButton(inst, false);
  }

  /* ── public: sound toggle ─────────────────────────────────── */

  function toggleSound(sectionId) {
    var inst = instances[sectionId];
    if (!inst) return;

    // The click itself IS the user activation the browser was waiting for.
    userActivated = true;

    if (!inst.media) mount(inst);

    var turningOn = !(inst.wantsSound && inst.currentVolume > 0);
    inst.wantsSound = turningOn;

    if (turningOn) {
      if (inst.play) inst.play();
      applySound(inst, true);
    } else {
      fadeVolume(inst, inst.currentVolume, 0, function () {
        inst.setMuted(true);
      });
      updateButton(inst, false);
    }
  }

  /* ── init ─────────────────────────────────────────────────── */

  function mount(inst) {
    if (inst.mode === 'external') mountExternal(inst);
    else mountNative(inst);
  }

  function setup(root) {
    var sectionId = root.getAttribute('data-section-id');
    if (!sectionId || instances[sectionId]) return;

    var inst = {
      root: root,
      sectionId: sectionId,
      prefix: root.getAttribute('data-class-prefix') || 'sl-section-' + sectionId,
      mode: root.getAttribute('data-mode') || 'shopify',
      breakpoint: parseInt(root.getAttribute('data-breakpoint'), 10) || 749,
      threshold: parseFloat(root.getAttribute('data-threshold')) || 0.5,
      fadeDuration: parseInt(root.getAttribute('data-fade'), 10),
      targetVolume: parseFloat(root.getAttribute('data-volume')),
      autoplay: readBool(root, 'data-autoplay', true),
      wantsSound: readBool(root, 'data-sound', true),
      loop: readBool(root, 'data-loop', true),
      controls: readBool(root, 'data-controls', false),
      label: root.closest('section') ? root.closest('section').getAttribute('aria-label') : '',
      soundBtn: root.querySelector('[data-sl-video-sound]'),
      media: null,
      mounting: false,
      loadedFor: null,
      inView: false,
      currentVolume: 0,
      fadeFrame: null,
      observer: null,
      mql: null,
      setVolume: function () {},
      setMuted: function () {},
      play: function () {},
      pause: function () {}
    };

    if (isNaN(inst.fadeDuration)) inst.fadeDuration = 800;
    if (isNaN(inst.targetVolume)) inst.targetVolume = 0.8;

    instances[sectionId] = inst;

    // Mount early enough that the first frame is decoded before it is seen.
    var preObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            mount(inst);
            preObserver.disconnect();
          }
        });
      },
      { rootMargin: '300px 0px' }
    );
    preObserver.observe(root);

    inst.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var visible = entry.isIntersecting && entry.intersectionRatio >= effectiveThreshold(inst, entry);
          if (visible === inst.inView) return;

          if (visible) {
            inst.inView = true;
            mount(inst);
            startPlayback(inst);
          } else {
            inst.inView = false;
            stopPlayback(inst);
          }
        });
      },
      // A ratio ladder, not a single stop: a video taller than the viewport
      // can never reach a fixed 0.5 ratio, so one threshold would never fire.
      { threshold: [0, 0.1, 0.25, 0.4, 0.5, 0.6, 0.75, 0.9, 1] }
    );
    inst.observer.observe(root);

    // Breakpoint changes swap the file (native only — external players
    // keep their embed; re-creating them mid-session is more disruptive
    // than the wrong-orientation file).
    if (window.matchMedia) {
      inst.mql = window.matchMedia('(max-width: ' + inst.breakpoint + 'px)');
      var onChange = function () {
        if (inst.mode !== 'external') remountNative(inst);
      };
      if (inst.mql.addEventListener) inst.mql.addEventListener('change', onChange);
      else if (inst.mql.addListener) inst.mql.addListener(onChange);
      inst.onBreakpointChange = onChange;
    }

    bindActivation();
  }

  function destroy(sectionId) {
    var inst = instances[sectionId];
    if (!inst) return;
    cancelFade(inst);
    if (inst.observer) inst.observer.disconnect();
    if (inst.mql && inst.onBreakpointChange) {
      if (inst.mql.removeEventListener) inst.mql.removeEventListener('change', inst.onBreakpointChange);
      else if (inst.mql.removeListener) inst.mql.removeListener(inst.onBreakpointChange);
    }
    if (inst.media) {
      try {
        if (inst.mode === 'external' && inst.media.destroy) inst.media.destroy();
        else if (inst.pause) inst.pause();
      } catch (e) {
        /* player already gone */
      }
    }
    delete instances[sectionId];
  }

  function scan(scope) {
    var nodes = (scope || document).querySelectorAll('[data-sl-video]');
    Array.prototype.forEach.call(nodes, setup);
  }

  window.slVideo = {
    __loaded: true,
    toggleSound: toggleSound,
    scan: scan,
    destroy: destroy
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan();
    });
  } else {
    scan();
  }

  // Theme Editor lifecycle
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-sl-video]');
    if (root) destroy(root.getAttribute('data-section-id'));
  });
})();
